import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PostHogProvider } from '@posthog/react';
import type { PostHogInterface } from 'posthog-js';
import { AuthProvider } from './contexts/AuthContext';
import { queryClient } from './queries/queryClient';
import { AppRoutes } from './routes';

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const deployEnv =
  (import.meta.env.VITE_PUBLIC_DEPLOY_ENV as string | undefined) ??
  (import.meta.env.MODE as string | undefined) ??
  'unknown';
const sessionRecordingEnabled = import.meta.env.VITE_PUBLIC_POSTHOG_SESSION_RECORDING === 'true';

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2026-01-30',
  disable_session_recording: !sessionRecordingEnabled,
  ...(sessionRecordingEnabled
    ? {
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
          },
        },
      }
    : {}),
  loaded: (posthog: PostHogInterface) => {
    // Super property: attached to every event for easy env segmentation
    posthog.register({ deploy_env: deployEnv });
    if (!sessionRecordingEnabled) {
      posthog.stopSessionRecording();
    }
  },
} as const;

const content = (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> : null}
    </QueryClientProvider>
  </BrowserRouter>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {posthogKey ? (
      <PostHogProvider apiKey={posthogKey} options={options}>
        {content}
      </PostHogProvider>
    ) : (
      content
    )}
  </React.StrictMode>,
);
